import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core';

import doubleListStyles from '../../../../../core/themes/utils/doubleListStyles';

import TreeListButtons from './TreeListButtons';
import TreeList from './TreeList';

import {
  cleanSelectedNodes,
  copyState,
  filterByNodeId,
  filterData,
  findByFundId,
  findByFundInvestorTagId,
  findNode,
  findParentNodes,
  getSelectedNodes,
  handleFundFamilyNode,
  handleFundNode,
  mergeArrays,
  removeFundInvestorTags,
  transferFundInvestorTags
} from './utils.js';
import { useClassesCustom } from '../styles';
import { useField } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import { cloneDeep } from 'lodash';

const FundInvestorTagLevelTree = ({
  classes,
  leftList,
  setLeftList,
  data,
  fundFamilies,
  fundTags,
  setIsJapanFundSelected,
  stepReadOnly,
  resourceupload,
  formRenderProps
}) => {
  const classesCustom = useClassesCustom();

  const [leftNodeIdsExpanded, setLeftNodeIdsExpanded] = useState([]);
  const [leftNodeIdsSelected, setLeftNodeIdsSelected] = useState([]);

  const [rightNodeIdsExpanded, setRightNodeIdsExpanded] = useState([]);
  const [rightNodeIdsSelected, setRightNodeIdsSelected] = useState([]);
  const [rightList, setRightList] = useState([]);

  const [selectedFundFamily, setSelectedFundFamily] = useState(null);
  const [selectedFundTags, setSelectedFundTags] = useState([]);
  const [searchQueryLeft, setSearchQueryLeft] = useState('');
  const [searchQueryRight, setSearchQueryRight] = useState('');

  const [selectionError, setSelectionError] = useState(false);

  const deepEqual = (a, b) => isEqual(a, b);

  /*
  Customizing the comparison function to address shallow equality. The default behavior compares references, not values,
  potentially causing inaccuracies in the pristine state of the form.
  */
  const { input: fundIdsInput } = useField('fundIds', { isEqual: deepEqual });
  const { input: fundInvestorTagIdInput } = useField('fundInvestorTagId');

  // If the resourceUpload has already funds and/or fundInvestorTag attached to it, we need to populate the right-hand list.
  useEffect(() => {
    if (resourceupload.fundInvestorTagId) {
      const fiTag = findByFundInvestorTagId(
        data,
        resourceupload.fundInvestorTagId,
        resourceupload.fundIds[0]
      );
      if (fiTag) {
        const fiTagTree = findParentNodes(data, fiTag.nodeId, true);
        setLeftList(prev => filterByNodeId(prev, fiTag.nodeId));
        setRightList(fiTagTree);
      }
    }

    if (
      !resourceupload.fundInvestorTagId &&
      resourceupload.fundIds.length > 0
    ) {
      let tree = [];
      resourceupload.fundIds.forEach(fundId => {
        const fundNode = findByFundId(data, fundId);
        const fundTree =
          fundNode && findParentNodes(data, fundNode.nodeId, false);
        setLeftList(prev => filterByNodeId(prev, fundNode.nodeId));
        if (tree.length === 0 || fundTree[0].fundNode) {
          tree.push(...fundTree);
        } else {
          const ffIndex = tree.findIndex(
            item => item.fundFamilyId === fundTree[0].fundFamilyId
          );
          ffIndex !== -1
            ? (tree[ffIndex].funds = tree[ffIndex].funds.concat(
                fundTree[0].funds
              ))
            : tree.push(...fundTree);
        }
      });
      setRightList(prev => [...prev, ...tree]);
    }
  }, [
    data,
    resourceupload.fundIds,
    resourceupload.fundInvestorTagId,
    setLeftList
  ]);

  // Expand Fund-Families by default.
  useEffect(() => {
    leftList.forEach(node =>
      node.fundFamilyNode
        ? setLeftNodeIdsExpanded(prev => [...prev, node.nodeId])
        : null
    );

    if (rightList.length > 0) {
      rightList.forEach(node =>
        node.fundFamilyNode
          ? setRightNodeIdsExpanded(prev => [...prev, node.nodeId])
          : null
      );
      // We also want to expand the associated Fund of a selected Fund-Investor tag.
      if (!isListFundLevel(rightList)) {
        rightList[0].fundFamilyNode
          ? setRightNodeIdsExpanded(prev => [
              ...prev,
              rightList[0].funds[0].nodeId
            ])
          : setRightNodeIdsExpanded(prev => [...prev, rightList[0].nodeId]);
      }
    }
  }, [leftList, rightList]);

  useEffect(() => {
    // It is requested by Neuberger (ADO-825793, ADO-899178) that when uploading a document, Fund Admins at Neuberger
    //needs to be displayed a warning message if they select Toranomon Private Equity 1 AIV, L.P. fund
    const isNBGClient = sessionStorage.getItem('tenant') === 'NeubergerBerman';
    // Toranomon Private Equity 1 AIV, L.P. fund (fund with fundId = fi_f_2FA1E30FA6EF436)
    if (isNBGClient && findByFundId(rightList, 'fi_f_2FA1E30FA6EF436')) {
      setIsJapanFundSelected(true);
    } else {
      setIsJapanFundSelected(false);
    }
  }, [rightList, setIsJapanFundSelected]);

  useEffect(() => {
    setSelectionError(!isListFundLevel(rightList));
  }, [rightList]);

  const isListFundLevel = list => {
    return list.every(node => {
      if (node.fundFamilyNode) {
        return node.funds.some(
          fundNode => fundNode.fundInvestorTags.length === 0
        );
      } else {
        return node.fundInvestorTags.length === 0;
      }
    });
  };

  const handleAddClick = () => {
    if (
      (leftNodeIdsSelected && leftNodeIdsSelected.length === 0) ||
      !isListFundLevel(rightList)
    )
      return;

    // Create copies of the states to avoid updating state inside the loop.
    let { copyRightList, copyLeftList } = copyState(rightList, leftList);

    // Create copies of the form values (same reason as above)
    let fundIdsSelected = fundIdsInput.value;
    let fundInvestorTagIdSelected = fundInvestorTagIdInput.value;

    const selectedNodes = getSelectedNodes(leftNodeIdsSelected, copyLeftList);

    // Filter selected leaves, removing funds belonging to selected Fund Family
    const cleanedSelectedNodes = cleanSelectedNodes(selectedNodes);

    cleanedSelectedNodes.forEach(node => {
      if (node.fundFamilyNode) {
        const [fundFamilyWithoutFiTag] = removeFundInvestorTags([node]);
        fundIdsSelected = [
          ...fundIdsSelected,
          ...fundFamilyWithoutFiTag.funds.map(fund => fund.id)
        ];
        const {
          currentList: leftListUpdated,
          oppositeList: rightListUpdated
        } = handleFundFamilyNode(
          fundFamilyWithoutFiTag,
          node.nodeId,
          copyRightList,
          copyLeftList
        );
        copyLeftList = cloneDeep(leftListUpdated);
        copyRightList = cloneDeep(rightListUpdated);
      }

      if (node.fundNode) {
        const leafParent = findParentNodes(copyLeftList, node.nodeId, false);
        fundIdsSelected = [...fundIdsSelected, node.fundId];
        const {
          currentList: leftListUpdated,
          oppositeList: rightListUpdated
        } = handleFundNode(leafParent, node, copyRightList, copyLeftList);
        copyLeftList = cloneDeep(leftListUpdated);
        copyRightList = cloneDeep(rightListUpdated);
      }

      if (node.fundInvestorTagNode && rightList.length === 0) {
        copyRightList = findParentNodes(copyLeftList, node.nodeId, true);
        copyLeftList = filterByNodeId(copyLeftList, node.nodeId);
        fundIdsSelected = [node.fundId];
        fundInvestorTagIdSelected = node.id;
      }
    });

    // Updating the lists after the loop
    setRightList(copyRightList);
    setLeftList(copyLeftList);

    // Updating the form values after the loop
    fundIdsInput.onChange(fundIdsSelected);
    fundInvestorTagIdInput.onChange(fundInvestorTagIdSelected);

    setLeftNodeIdsSelected([]);
  };

  const handleAddAllClick = () => {
    if (isListFundLevel(rightList)) {
      const leftListWithoutFiTag = removeFundInvestorTags(
        leftList.filter(node =>
          filterData(
            node,
            searchQueryLeft,
            selectedFundFamily?.id,
            selectedFundTags
          )
        )
      );

      const fundIds = leftListWithoutFiTag.flatMap(leftListItem => {
        if (leftListItem.fundNode) {
          return leftListItem.fundId;
        } else {
          return leftListItem.funds.map(fund => fund.fundId);
        }
      });

      const updatedFundIdInputValue = [...fundIdsInput.value, ...fundIds];
      fundIdsInput.onChange(updatedFundIdInputValue);

      const updatedRightList = mergeArrays(rightList, leftListWithoutFiTag);
      const nodeIdsToFilter = leftListWithoutFiTag.map(item => item.nodeId);
      setRightList(updatedRightList);
      setLeftList(
        leftList.filter(item => !nodeIdsToFilter.includes(item.nodeId))
      );
    }
  };

  const handleToggleLeft = (_event, nodeIds) => {
    setLeftNodeIdsExpanded(nodeIds);
  };

  const handleToggleRight = (_event, nodeIds) => {
    setRightNodeIdsExpanded(nodeIds);
  };

  const handleSelectLeft = (_event, nodeId) => {
    let selectedLeaves = [];

    nodeId &&
      nodeId.forEach(node => {
        selectedLeaves.push(...findNode(data, node));
      });

    // Set an error if the selection is invalid (FiT + fund(s)/fundFamily(ies)).
    setSelectionError(
      (selectedLeaves.length > 1 &&
        selectedLeaves.some(leaf => leaf.fundInvestorTagNode)) ||
        (rightList.length > 0 &&
          isListFundLevel(rightList) &&
          selectedLeaves.some(leaf => leaf.fundInvestorTagNode)) ||
        (rightList.length > 0 && !isListFundLevel(rightList))
    );

    setLeftNodeIdsSelected(nodeId);
  };

  const handleSelectRight = (_event, nodeId) => {
    setRightNodeIdsSelected(nodeId);
  };

  const handleRemoveAllClick = () => {
    setLeftNodeIdsSelected([]);
    setRightNodeIdsSelected([]);

    const nodesFromRightList = rightList.filter(node =>
      filterData(node, searchQueryRight, null, [])
    );

    // Processing when there is only one Fund-Investor Tag in the right list.
    if (!isListFundLevel(rightList) && nodesFromRightList.length > 0) {
      const fiTag = nodesFromRightList[0].fundFamilyNode
        ? nodesFromRightList[0].funds[0].fundInvestorTags[0].nodeId
        : nodesFromRightList[0].fundInvestorTags[0].nodeId;
      const fiTagOriginalParent = findParentNodes(data, fiTag, true);
      setLeftList(transferFundInvestorTags(fiTagOriginalParent, leftList));
      const fiTagRightParent = findParentNodes(rightList, fiTag);
      setRightList(filterByNodeId(rightList, fiTagRightParent[0].nodeId));
      fundInvestorTagIdInput.onChange(null);
      fundIdsInput.onChange([]);
      return;
    }

    let fundIdsToRemove = [];
    let nodeIdsToRemove = [];
    for (const node of nodesFromRightList) {
      nodeIdsToRemove.push(node.nodeId);
      if (node.fundFamilyNode) {
        const nodeFundIds = node.funds.map(fund => fund.nodeId);
        fundIdsToRemove = [
          ...fundIdsToRemove,
          ...node.funds.map(fund => fund.fundId)
        ];
        const matchingFF = leftList.find(
          leftListItem => leftListItem.fundFamilyId === node.fundFamilyId
        );
        // If a Fund-Investor Tag is attached to a fund within the Fund Family node, we retrieve the original Fund Family.
        const originalFundFamily = data.find(
          dataItem => dataItem.fundFamilyId === node.fundFamilyId
        );
        // From the original Fund Family, we select only the Funds that are present in the current node.
        const fundsWithFiTag = originalFundFamily.funds.filter(fund =>
          nodeFundIds.includes(fund.nodeId)
        );

        if (matchingFF) {
          setLeftList(prev =>
            prev.map(leftListNode => {
              if (leftListNode.fundFamilyId === node.fundFamilyId) {
                return {
                  ...leftListNode,
                  funds: leftListNode.funds.concat(fundsWithFiTag)
                };
              } else {
                return leftListNode;
              }
            })
          );
        } else {
          setLeftList(prev => [...prev, originalFundFamily]);
        }
      } else {
        // Similarly, if the Fund had Fund-Investor Tag(s) attached to it, we need to retrieve the original Fund.
        const matchingFund = data.find(
          dataItem => dataItem.nodeId === node.nodeId
        );

        fundIdsToRemove.push(node.fundId);

        setLeftList(prev => [...prev, matchingFund]);
      }
    }

    setRightList(
      rightList.filter(item => !nodeIdsToRemove.includes(item.nodeId))
    );
    fundIdsInput.onChange(
      fundIdsInput.value.filter(fundId => !fundIdsToRemove.includes(fundId))
    );
  };

  const handleRemoveClick = () => {
    if (rightNodeIdsSelected && rightNodeIdsSelected.length === 0) return;

    // Create copies of the states to avoid updating state inside the loop.
    let { copyRightList, copyLeftList } = copyState(rightList, leftList);

    // Create copies of the form values (same reason as above)
    let fundIdsInputToUpdate = fundIdsInput.value;
    let fundInvestorTagIdInputToUpdate = fundInvestorTagIdInput.value;

    const selectedNodes = getSelectedNodes(rightNodeIdsSelected, copyRightList);

    // Filter selected leaves, removing funds belonging to selected Fund Family
    const cleanedSelectedNodes = cleanSelectedNodes(selectedNodes);

    cleanedSelectedNodes.forEach(node => {
      if (node.fundFamilyNode) {
        if (node.funds[0].fundInvestorTags.length > 0) {
          copyLeftList = transferFundInvestorTags([node], copyLeftList);
          copyRightList = filterByNodeId(copyRightList, node.nodeId);
          fundIdsInputToUpdate = [];
          fundInvestorTagIdInputToUpdate = null;
        } else {
          // We retrieve potential Fund-Investor tag(s) that might have been removed.
          const originalNode = findNode(data, node.nodeId).map(item => {
            if (item.fundFamilyNode) {
              // Only keeping the funds present in the right-hand list.
              const updatedFunds = item.funds.filter(fund =>
                node.funds.some(
                  fundToRemove => fundToRemove.fundId === fund.fundId
                )
              );
              const fundIdsToRemove = updatedFunds.map(fund => fund.fundId);
              fundIdsInputToUpdate = fundIdsInputToUpdate.filter(
                fundId => !fundIdsToRemove.includes(fundId)
              );
              return {
                ...item,
                funds: updatedFunds
              };
            }
            return item;
          });
          const {
            currentList: rightListUpdated,
            oppositeList: leftListUpdated
          } = handleFundFamilyNode(
            ...originalNode,
            node.nodeId,
            copyLeftList,
            copyRightList
          );
          copyLeftList = cloneDeep(leftListUpdated);
          copyRightList = cloneDeep(rightListUpdated);
        }
      }

      if (node.fundNode) {
        if (node.fundInvestorTags.length > 0) {
          const parentNode = findParentNodes(data, node.nodeId, true);
          copyLeftList = transferFundInvestorTags(parentNode, copyLeftList);
          copyRightList = filterByNodeId(copyRightList, node.nodeId);
          fundIdsInputToUpdate = [];
          fundInvestorTagIdInputToUpdate = null;
        } else {
          const leafParent = findParentNodes(data, node.nodeId, true);
          fundIdsInputToUpdate = fundIdsInputToUpdate.filter(
            fundId => fundId !== node.fundId
          );
          const {
            currentList: rightListUpdated,
            oppositeList: leftListUpdated
          } = handleFundNode(leafParent, node, copyLeftList, copyRightList);
          copyLeftList = cloneDeep(leftListUpdated);
          copyRightList = cloneDeep(rightListUpdated);
        }
      }

      if (node.fundInvestorTagNode) {
        const treeSelected = findParentNodes(data, node.nodeId, true);
        copyLeftList = transferFundInvestorTags(treeSelected, leftList);
        copyRightList = [];
        fundIdsInputToUpdate = [];
        fundInvestorTagIdInputToUpdate = null;
      }
    });

    // Updating the lists after the loop
    setRightList(copyRightList);
    setLeftList(copyLeftList);

    // Updating the form values after the loop
    fundIdsInput.onChange(fundIdsInputToUpdate);
    fundInvestorTagIdInput.onChange(fundInvestorTagIdInputToUpdate);

    setRightNodeIdsSelected([]);
  };

  const handleFundFamilyChange = (_, value) => {
    setSelectedFundFamily(value);
  };

  const onFundTagsChange = e => {
    setSelectedFundTags(e);
  };

  return (
    <>
      <TreeList
        classes={classes}
        classesCustom={classesCustom}
        searchFundNameQuery={searchQueryLeft}
        setSearchFundNameQuery={setSearchQueryLeft}
        fundTags={fundTags}
        fundFamilies={fundFamilies}
        selectedFundFamily={selectedFundFamily}
        handleFundFamilyChange={handleFundFamilyChange}
        selectedFundTags={selectedFundTags}
        onFundTagsChange={onFundTagsChange}
        nodesExpanded={leftNodeIdsExpanded}
        nodesSelected={leftNodeIdsSelected}
        handleToggle={handleToggleLeft}
        handleSelect={handleSelectLeft}
        list={leftList}
        listTitle="Available Funds"
        listSide="left"
        handleDoubleClick={handleAddClick}
        stepReadOnly={stepReadOnly}
      />
      <TreeListButtons
        classes={classes}
        handleAddClick={handleAddClick}
        handleAddAllClick={handleAddAllClick}
        handleRemoveClick={handleRemoveClick}
        handleRemoveAllClick={handleRemoveAllClick}
        stepReadOnly={stepReadOnly}
        selectionError={selectionError}
      />
      <TreeList
        classes={classes}
        classesCustom={classesCustom}
        searchFundNameQuery={searchQueryRight}
        setSearchFundNameQuery={setSearchQueryRight}
        fundTags={[]}
        fundFamilies={[]}
        selectedFundFamily={null}
        selectedFundTags={[]}
        nodesExpanded={rightNodeIdsExpanded}
        nodesSelected={rightNodeIdsSelected}
        handleToggle={handleToggleRight}
        handleSelect={handleSelectRight}
        list={rightList}
        listTitle="Selected Funds *"
        listSide="right"
        data={data}
        handleDoubleClick={handleRemoveClick}
        stepReadOnly={stepReadOnly}
        formRenderProps={formRenderProps}
      />
    </>
  );
};

export default withStyles(doubleListStyles)(FundInvestorTagLevelTree);
